import type { RenderedDomContext } from '@eigenpal/docx-editor-react/plugin-api';
import { SEVERITY_TONE, type ResolvedFix } from './documentFixes';

/**
 * The non-persistent overlay drawn over the rendered pages (passed to `<DocxEditor pluginOverlays>`). It draws
 * a severity-tinted box over each located finding; the fix cards themselves live in the always-visible list
 * beside the document. Clicking a highlight activates its card; the active one gets a stronger outline. None of
 * this touches the ProseMirror document, so it can never serialize into the saved .docx.
 */
export function DocxFixOverlay({
  resolved,
  ctx,
  activeId,
  onActivate,
  onReopen,
}: {
  resolved: ResolvedFix[];
  ctx: RenderedDomContext;
  activeId: string | null;
  onActivate: (id: string | null) => void;
  /** Clicking the highlighted snippet reopens its (possibly closed) card. */
  onReopen: (id: string) => void;
}) {
  const offset = ctx.getContainerOffset();

  return (
    <>
      {resolved.map(({ fix, range }) => {
        if (!range) return null;
        const rects = ctx.getRectsForRange(range.from, range.to);
        if (!rects.length) return null;
        const tone = SEVERITY_TONE[fix.severity];
        const isActive = fix.id === activeId;

        return (
          <div key={fix.id} data-fix-id={fix.id}>
            {rects.map((r, i) => (
              <div
                key={i}
                onClick={() => onReopen(fix.id)}
                onMouseEnter={() => onActivate(fix.id)}
                onMouseLeave={() => onActivate(null)}
                style={{
                  position: 'absolute',
                  left: offset.x + r.x - 1,
                  top: offset.y + r.y,
                  width: r.width + 2,
                  height: r.height,
                  // Fill only (no underline); the hovered card lightens its highlight.
                  background: isActive ? tone.fillHover : tone.fill,
                  borderRadius: 3,
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  transition: 'background 0.12s ease',
                }}
              />
            ))}
          </div>
        );
      })}
    </>
  );
}
